import { geocodeReverse } from '@/services/naaviApi/internalService';
import type { GeocodeResponse } from '@/services/naaviApi/types';

const LOCATION_STORAGE_KEY = 'market:lastLocation';
const GEO_PROMPT_SESSION_KEY = 'market:geoPrompt:attempted';

export type StoredLocation = {
  latitude: number;
  longitude: number;
  formattedAddress?: string | null;
  placeId?: string | null;
  timestamp: number;
  radiusKm?: number | null;
};

export function loadStoredLocation(): StoredLocation | null {
  try {
    const raw = localStorage.getItem(LOCATION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredLocation;
    if (typeof parsed.latitude !== 'number' || typeof parsed.longitude !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveStoredLocation(location: StoredLocation): void {
  try {
    localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));
  } catch (error) {
    void error;
  }
}

export function clearStoredLocation(): void {
  localStorage.removeItem(LOCATION_STORAGE_KEY);
}

type BrowserLocationOptions = {
  language?: string;
  timeoutMs?: number;
  skipReverseGeocode?: boolean;
};

export async function requestBrowserLocation(options: BrowserLocationOptions = {}): Promise<StoredLocation | null> {
  if (!('geolocation' in navigator)) {
    return null;
  }

  const { language = navigator.language, timeoutMs = 15000, skipReverseGeocode = false } = options;

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;

      if (skipReverseGeocode) {
        const stored: StoredLocation = {
          latitude,
          longitude,
          formattedAddress: null,
          placeId: null,
          timestamp: Date.now(),
          radiusKm: 10,
        };
        saveStoredLocation(stored);
        resolve(stored);
        return;
      }

      try {
        const response = await geocodeReverse({ latitude, longitude, language });
        const result: GeocodeResponse | undefined = response || undefined;
        const stored: StoredLocation = {
          latitude,
          longitude,
          formattedAddress: result?.formattedAddress ?? null,
          placeId: result?.placeId ?? null,
          timestamp: Date.now(),
          radiusKm: 10
        };
        saveStoredLocation(stored);
        resolve(stored);
      } catch {
        const fallback: StoredLocation = {
          latitude,
          longitude,
          timestamp: Date.now(),
          radiusKm: 10
        };
        saveStoredLocation(fallback);
        resolve(fallback);
      }
    }, () => {
      resolve(null);
    }, {
      enableHighAccuracy: true,
      timeout: timeoutMs,
      maximumAge: 0
    });
  });
}

export async function ensureInitialGeolocation(language?: string): Promise<StoredLocation | null> {
  if (sessionStorage.getItem(GEO_PROMPT_SESSION_KEY) === 'attempted') {
    return loadStoredLocation();
  }

  sessionStorage.setItem(GEO_PROMPT_SESSION_KEY, 'attempted');
  return requestBrowserLocation({ language });
}
